import { capitalize, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import GenericAutocomplete from 'ui/DropdownItems/GenericAutocomplete';
import ResetWrapper from 'ui/DropdownItems/ResetWrapper';
import { AutocompletAble, ModalFormItem } from 'ui/DropdownItems/types';
import {
    getFormattedBpsOptions,
    getResourcesTranslation,
    setAvailableResourcesForBps,
    sortBpsOptions,
} from '../../DuplicateSettings/helpers';
import { BudgetBys } from 'business/constants';

type Props = {
    selectedTargetBudgetProposalIds:
        | AutocompletAble<BudgetProposalWithResourcesT>[]
        | null;
    setSelectedTargetBudgetProposalIds: DispatchState<
        AutocompletAble<BudgetProposalWithResourcesT>[] | null
    >;
    selectedTargetResourceSoftwareIds: AutocompletAble<string>[] | null;
    setSelectedTargetResourceSoftwareIds: DispatchState<
        AutocompletAble<string>[] | null
    >;
    budgetProposals: BudgetProposalWithResourcesT[];
    by: BudgetBys;
};

export default function SecondDuplicateArgumentsStep({
    selectedTargetBudgetProposalIds,
    setSelectedTargetBudgetProposalIds,
    selectedTargetResourceSoftwareIds,
    setSelectedTargetResourceSoftwareIds,
    budgetProposals,
    by: selectedBy,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const availableBPs = getFormattedBpsOptions(budgetProposals);
    const [availableResources, setAvailableResources] = useState<
        AutocompletAble<string>[]
    >([]);
    useEffect(() => {
        setAvailableResourcesForBps(
            budgetProposals,
            selectedTargetBudgetProposalIds,
            setAvailableResources,
            true,
        );
    }, [selectedTargetBudgetProposalIds]);

    const autocompletes: Array<
        ModalFormItem<string | BudgetProposalWithResourcesT>
    > = [
        {
            children: (
                <GenericAutocomplete
                    multiple
                    selectedValues={selectedTargetBudgetProposalIds}
                    availableValues={availableBPs}
                    setSelectedValues={setSelectedTargetBudgetProposalIds}
                    label={t('adminBudgetSpace.duplicateArguments.targetBps')}
                    shouldSortOptions
                    sortItems={sortBpsOptions}
                    key="selectedTargetBudgetProposalIds"
                />
            ),
            value: selectedTargetBudgetProposalIds,
            setValue: setSelectedTargetBudgetProposalIds,
        },
        {
            children: (
                <GenericAutocomplete
                    multiple
                    selectedValues={selectedTargetResourceSoftwareIds}
                    availableValues={availableResources}
                    setSelectedValues={setSelectedTargetResourceSoftwareIds}
                    label={capitalize(
                        t(
                            'adminBudgetSpace.duplicateArguments.targetResources',
                            {
                                resources: getResourcesTranslation(selectedBy),
                            },
                        ),
                    )}
                    shouldSortOptions
                    key="selectedTargetResourceSoftwareIds"
                />
            ),
            value: selectedTargetResourceSoftwareIds,
            setValue: setSelectedTargetResourceSoftwareIds,
        },
    ];

    return (
        <>
            <Typography variant="h4" sx={{ paddingBottom: '1.5em' }}>
                {t('adminBudgetSpace.duplicateArguments.title')}
            </Typography>
            <Typography sx={{ whiteSpace: 'break-spaces' }}>
                {t('adminBudgetSpace.duplicateArguments.targetText', {
                    resources: getResourcesTranslation(selectedBy),
                })}
            </Typography>
            <ResetWrapper items={autocompletes} />
        </>
    );
}
